/**
 * Explanatory tooltip copy for PMEI parameter-table cells. This is the wording counterpart to
 * `PMEICellColorGrade.ts`: where the grade collapses a multi-value cell to a single worst-case
 * color, the tooltip describes every value. Data parameters (Confirmed/Measured), material inputs
 * (Confirmed), and equipment (Confirmed) share the same layout primitives and consume the same
 * deviation predicates the grade uses, so a cell's color and its copy can never disagree.
 *
 * Tooltip presence is a superset of warning-grade presence: every graded difference gets a tooltip,
 * and an unrecorded cell (no value records at all in that column) also gets an "isn't confirmed/measured
 * yet" message even though it earns no color. A recorded-but-empty typed value is not unrecorded and
 * does not get that message.
 *
 * A multi-value cell with at least one difference splits into two sections: the values differing
 * from plan (each a warning sentence), then every value listed plainly. Single-value cells and the
 * parameter-level empty cases stay a single unsectioned line.
 */
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';

import {
  assertAtMostOnePlannedEquipment,
  classifyDataParameterDifference,
  classifyEquipmentDifference,
  type EquipmentValue,
  confirmedMaterialMatchesPlan,
  hasMultiplePlannedMaterialInputValues,
  hasMultiplePlannedValues,
  type MaterialInputValue,
  isPresentTypedValue,
  isRecordedMaterialInputValue,
} from './PMEICellColorGrade';
import {formatMaterialInputAmount, formatMaterialInputValue} from './PMEIMaterialInputFormat';
import type {MinMaxTypedValue, RangeComparison} from './PMEIParameterRange';

/** The two data-parameter columns that receive tooltips; drives both the verb and the capitalized label. */
export type ColumnKind = 'confirmed' | 'measured';

const COLUMN_LABEL: Record<ColumnKind, string> = {
  confirmed: 'Confirmed',
  measured: 'Measured',
};

/** Heading for the section that lists every recorded value plainly, below the differences section. */
const ALL_VALUES_HEADING = 'All values';

/**
 * A run of tooltip text; `emphasis` marks the dynamic value/name/bound tokens that a difference
 * message bolds so they stand out from the surrounding words. Parameter-level messages
 * (empty/unplanned) and the bare valid-value line use a single plain segment.
 */
export interface TooltipTextSegment {
  text: string;
  emphasis?: boolean;
}

/** A single line of tooltip text, built from plain and emphasized segments. */
export interface TooltipLine {
  segments: Array<TooltipTextSegment>;
}

/** A titled group of tooltip lines — one of the two sections a multi-value differing cell splits into. */
export interface TooltipSection {
  heading: string;
  lines: Array<TooltipLine>;
}

/**
 * A cell tooltip is either a single unsectioned line — the empty/unplanned parameter-level messages
 * and the lone warning sentence for a single-value cell that differs — or two sections for a
 * multi-value cell with at least one difference (differences first, then all values).
 */
export type CellTooltipContent =
  | {kind: 'message'; message: TooltipLine}
  | {kind: 'sections'; sections: Array<TooltipSection>};

/** The values that are actually present — non-null and not the type's empty sentinel. */
const presentValues = (values: Array<TypedValue | null>): Array<TypedValue> =>
  values.filter((v) => isPresentTypedValue(v));

/** A run of plain (non-emphasized) tooltip text. */
function plain(text: string): TooltipTextSegment {
  return {text};
}

/** A run of emphasized (bold) tooltip text — the dynamic value/name/bound tokens in a difference. */
function bold(text: string): TooltipTextSegment {
  return {text, emphasis: true};
}

/** A single line of tooltip text from its segments. */
function line(segments: Array<TooltipTextSegment>): TooltipLine {
  return {segments};
}

/** A single unsectioned line — empty/unplanned messages and the single-value difference sentence. */
function message(messageLine: TooltipLine): CellTooltipContent {
  return {kind: 'message', message: messageLine};
}

/** The two-section content for a multi-value cell with at least one difference. */
function sectioned(sections: Array<TooltipSection>): CellTooltipContent {
  return {kind: 'sections', sections};
}

/** Heading for the differences section, e.g. "3 values differing from plan" / "1 value differing from plan". */
function differencesHeading(differingCount: number): string {
  const valueNoun = differingCount === 1 ? 'value' : 'values';
  return `${differingCount} ${valueNoun} differing from plan`;
}

/**
 * Computes the tooltip content for a data-parameter cell (Confirmed or Measured), or null when the
 * cell should have no tooltip. The flow parallels `gradeDimension`:
 * - more than one planned setpoint → null, matching the ungraded grade;
 * - no value records at all → the empty rule ("… isn't confirmed yet");
 * - recorded values but no setpoint → the unplanned rule ("… was confirmed without a planned setpoint");
 * - recorded values against a single setpoint → a warning line per differing value; null when nothing
 *   differs, a bare sentence when a single-value cell differs, otherwise two sections.
 */
export function computeDataParameterCellTooltip(params: {
  parameterName: string;
  columnKind: ColumnKind;
  cellValues: Array<TypedValue | null>;
  plannedValues: Array<TypedValue | null>;
  minimum?: MinMaxTypedValue;
  maximum?: MinMaxTypedValue;
}): CellTooltipContent | null {
  const {parameterName, columnKind, cellValues, plannedValues, minimum = null, maximum = null} = params;

  if (hasMultiplePlannedValues(plannedValues)) {
    // With more than one planned value there is no single target to compare against, so the cell is
    // never graded and gets no tooltip either.
    return null;
  }

  if (cellValues.length === 0) {
    // No value records at all — the same unrecorded case the placeholder covers.
    return message(emptyMessage(parameterName, columnKind));
  }

  const nonEmptyCell = presentValues(cellValues);
  const nonEmptyPlanned = presentValues(plannedValues);

  if (nonEmptyCell.length === 0) {
    // Value record(s) exist but every typed value is empty; the column was filled in, so no
    // "isn't confirmed/measured yet" message.
    return null;
  }

  if (nonEmptyPlanned.length === 0) {
    return message(unplannedMessage(parameterName, columnKind));
  }

  const planned = nonEmptyPlanned[0];
  const label = COLUMN_LABEL[columnKind];
  // One warning line per value that differs from plan, null for a value that matches.
  const warnings = nonEmptyCell.map((value) => warningLineFor({value, planned, minimum, maximum, label}));
  const differingLines = warnings.filter((w): w is TooltipLine => w != null);
  const allValueLines = nonEmptyCell.map((value) => line([plain(formatTypedValue(value))]));

  return buildDifferencesContent({
    recordedValueCount: nonEmptyCell.length,
    // One line per value here, so the count of differing lines is the count of differing values.
    differingValueCount: differingLines.length,
    differingLines,
    allValueLines,
  });
}

/**
 * Assembles the differences content shared by every parameter kind from its per-value difference
 * lines and the plain "all values" lines, keeping color and copy in lockstep:
 * - no differences → null (the cell earns no warning color either);
 * - a single recorded value with a single difference → one unsectioned warning line;
 * - any other differing cell → the two-section layout ("N values differing from plan", then every
 *   recorded value listed plainly under "All values").
 *
 * The heading reports `differingValueCount` — the number of recorded *values* that deviate — which the
 * caller supplies separately from `differingLines` because the two legitimately differ in number: a
 * single material-input value can contribute both a material and an amount line, so counting lines
 * would overstate how many values differ.
 */
function buildDifferencesContent({
  recordedValueCount,
  differingValueCount,
  differingLines,
  allValueLines,
}: {
  recordedValueCount: number;
  differingValueCount: number;
  differingLines: Array<TooltipLine>;
  allValueLines: Array<TooltipLine>;
}): CellTooltipContent | null {
  if (differingLines.length === 0) {
    // Every value is valid: color and tooltip stay in sync, so no differences tooltip.
    return null;
  }

  if (recordedValueCount === 1 && differingLines.length === 1) {
    // A single-value cell that differs shows just its warning sentence, with no section headings.
    return message(differingLines[0]);
  }

  return sectioned([
    {heading: differencesHeading(differingValueCount), lines: differingLines},
    // A multi-value cell with at least one difference: every recorded value as a plain line
    {heading: ALL_VALUES_HEADING, lines: allValueLines},
  ]);
}

/**
 * The warning line for one value against the single planned setpoint, or null when it satisfies the
 * plan. Maps the shared `classifyDataParameterDifference` verdict — the same one that drives the
 * color grade — to its wording, so an out-of-range value's line names the breached bound and a plain
 * mismatch names the missed setpoint.
 */
function warningLineFor({
  value,
  planned,
  minimum,
  maximum,
  label,
}: {
  value: TypedValue;
  planned: TypedValue;
  minimum: MinMaxTypedValue;
  maximum: MinMaxTypedValue;
  label: string;
}): TooltipLine | null {
  const difference = classifyDataParameterDifference(value, planned, minimum, maximum);
  if (difference == null) {
    return null;
  }
  if (difference.kind === 'outOfRange') {
    return outOfRangeMessage(label, value, difference.comparison, minimum, maximum);
  }
  return mismatchMessage(label, value, planned);
}

/**
 * "Confirmed value of **5** is below the minimum value of **10**" / "… above the maximum value of …".
 * The value and the bound it breaches are emphasized so the offending numbers stand out.
 */
function outOfRangeMessage(
  label: string,
  value: TypedValue,
  comparison: RangeComparison,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): TooltipLine {
  const direction = comparison.isBelowMinimum ? 'below' : 'above';
  const boundWord = comparison.isBelowMinimum ? 'minimum' : 'maximum';
  const bound = comparison.isBelowMinimum ? minimum : maximum;
  const boundText = bound != null ? formatTypedValue(bound) : '';
  return differenceLine(
    `${label} value of `,
    formatTypedValue(value),
    ` is ${direction} the ${boundWord} value of `,
    boundText
  );
}

/**
 * Message when there is a difference between two values.
 *
 * Takes the values as text so each caller owns its own formatting before calling this function.
 */
function differenceLine(
  prefix: string,
  confirmedText: string,
  infix: string,
  plannedText: string
): TooltipLine {
  return line([plain(prefix), bold(confirmedText), plain(infix), bold(plannedText)]);
}

function unplannedValueLine(prefix: string, valueText: string, suffix: string): TooltipLine {
  return line([plain(prefix), bold(valueText), plain(suffix)]);
}

/**
 * "Confirmed value of **38** does not match the planned setpoint of **37**". The recorded value
 * and the setpoint it misses are emphasized.
 */
function mismatchMessage(label: string, value: TypedValue, planned: TypedValue): TooltipLine {
  return differenceLine(
    `${label} value of `,
    formatTypedValue(value),
    ' does not match the planned setpoint of ',
    formatTypedValue(planned)
  );
}

function emptyMessage(parameterName: string, columnKind: ColumnKind): TooltipLine {
  return line([plain(`${parameterName} isn't ${columnKind} yet`)]);
}

function unplannedMessage(parameterName: string, columnKind: ColumnKind): TooltipLine {
  return line([plain(`${parameterName} was ${columnKind} without a planned setpoint`)]);
}

/**
 * The difference lines for one dimension (material or amount), together with the recorded values
 * those lines implicate. Returning the values themselves — rather than counting lines — is what lets
 * the heading say "N values differing from plan" without ever disagreeing with the lines beneath it:
 * a value that deviates in both dimensions lands in both sets and the union counts it once.
 *
 * The dimension is projected here via `selectDimension` rather than by the caller, so a line can never be
 * attributed to the wrong value.
 *
 * Returns no lines and no values when the dimension is ungraded or satisfies its plan. The caller has
 * already rejected parameters planning more than one value, so `plannedValues` holds at most one
 * present value.
 */
function dimensionDifferenceLines({
  values,
  selectDimension,
  plannedValues,
  isEquivalent,
  unconfirmedLine,
  unplannedLine,
  mismatchLine,
}: {
  values: Array<MaterialInputValue>;
  selectDimension: (value: MaterialInputValue) => TypedValue | null;
  plannedValues: Array<TypedValue | null>;
  isEquivalent: (confirmed: TypedValue, planned: TypedValue) => boolean;
  unconfirmedLine: () => TooltipLine;
  unplannedLine: (confirmed: TypedValue) => TooltipLine;
  mismatchLine?: (confirmed: TypedValue, planned: TypedValue) => TooltipLine;
}): DimensionDifferences {
  const nonEmptyPlanned = presentValues(plannedValues);

  // Pair each recorded value with the dimension being described, keeping only those that recorded
  // one. See DimensionEntry for why a line needs both halves.
  const present = values.flatMap((value) => {
    const dimension = selectDimension(value);
    return isPresentTypedValue(dimension) ? [{value, dimension}] : [];
  });

  if (present.length === 0) {
    if (nonEmptyPlanned.length !== 1) {
      // Nothing planned for this dimension, so nothing to have missed.
      return {lines: [], differingValues: new Set()};
    }
    // The dimension is blank for every recorded value, so one "isn't confirmed yet" line speaks for
    // all of them — the only branch where a single line implicates more than one value.
    return {lines: [unconfirmedLine()], differingValues: new Set(values)};
  }

  if (nonEmptyPlanned.length === 0) {
    return linePerValue(present, ({dimension}) => unplannedLine(dimension));
  }

  if (mismatchLine == null) {
    return {lines: [], differingValues: new Set()};
  }

  const planned = nonEmptyPlanned[0];
  return linePerValue(
    present.filter(({dimension}) => !isEquivalent(dimension, planned)),
    ({dimension}) => mismatchLine(dimension, planned)
  );
}

/** The difference lines for one dimension and the recorded values they describe. */
interface DimensionDifferences {
  lines: Array<TooltipLine>;
  differingValues: Set<MaterialInputValue>;
}

interface DimensionEntry {
  /** The whole recorded value, which the heading counts by identity. */
  value: MaterialInputValue;
  /** The one field of `value` this pass describes (its material or its amount), which the line names. */
  dimension: TypedValue;
}

/** One line per entry, each implicating only the recorded value it describes. */
function linePerValue(
  entries: Array<DimensionEntry>,
  lineFor: (entry: DimensionEntry) => TooltipLine
): DimensionDifferences {
  return {
    lines: entries.map((entry) => lineFor(entry)),
    differingValues: new Set(entries.map(({value}) => value)),
  };
}

/** A planned material left unconfirmed (the confirmed cell recorded an amount but no material). */
function materialUnconfirmedMessage(parameterName: string): TooltipLine {
  return line([plain(`${parameterName}'s material isn't confirmed yet`)]);
}

/** A planned amount left unconfirmed (the confirmed cell recorded a material but no amount). */
function amountUnconfirmedMessage(parameterName: string): TooltipLine {
  return line([plain(`${parameterName}'s amount isn't confirmed yet`)]);
}

/** "Confirmed material **glucose** has no planned material" — one per unplanned confirmed material. */
function materialUnplannedMessage(confirmedText: string): TooltipLine {
  return unplannedValueLine('Confirmed material ', confirmedText, ' has no planned material');
}

/** "Confirmed amount of **720 mL** has no planned amount" — one per unplanned confirmed amount. */
function amountUnplannedMessage(confirmedText: string): TooltipLine {
  return unplannedValueLine('Confirmed amount of ', confirmedText, ' has no planned amount');
}

/** "Confirmed material **X** does not match the planned material **Y**". */
function materialMismatchMessage(confirmed: TypedValue, planned: TypedValue): TooltipLine {
  return differenceLine(
    'Confirmed material ',
    formatTypedValue(confirmed),
    ' does not match the planned material ',
    formatTypedValue(planned)
  );
}

/**
 * Computes the tooltip content for a material-input Confirmed cell, or null when it should have no
 * tooltip. Material mismatches and unplanned materials are described; amount mismatches are
 * ignored, but a confirmed amount with no planned amount still earns an unplanned-amount line and a
 * planned amount with no confirmed amount still earns an "isn't confirmed yet" line (without grading).
 *
 * A parameter planning more than one value is skipped outright, matching the ungraded cell color.
 */
export function computeMaterialInputCellTooltip(params: {
  parameterName: string;
  cellValues: Array<MaterialInputValue>;
  plannedValues: Array<MaterialInputValue>;
  isEntity: boolean;
  amountUnit: UnitSummary | null;
}): CellTooltipContent | null {
  const {parameterName, cellValues, plannedValues, isEntity, amountUnit} = params;

  if (hasMultiplePlannedMaterialInputValues(plannedValues)) {
    return null;
  }

  if (cellValues.length === 0) {
    return emptyCellMessage(parameterName, plannedValues);
  }

  const recordedValues = cellValues.filter((v) => isRecordedMaterialInputValue(v));

  if (recordedValues.length === 0) {
    // Record(s) exist but every material and amount is empty.
    return null;
  }

  // The unit is a per-parameter fact, so it is resolved into a formatter once here rather than
  // travelling into each sentence builder.
  const formatAmount = (amount: TypedValue): string => formatMaterialInputAmount(amount, amountUnit) ?? '';

  const material = dimensionDifferenceLines({
    values: recordedValues,
    selectDimension: (v) => v.material,
    plannedValues: plannedValues.map((v) => v.material),
    isEquivalent: (confirmed, planned) => confirmedMaterialMatchesPlan(confirmed, planned, isEntity),
    unconfirmedLine: () => materialUnconfirmedMessage(parameterName),
    unplannedLine: (confirmed) => materialUnplannedMessage(formatTypedValue(confirmed)),
    mismatchLine: materialMismatchMessage,
  });
  const amount = dimensionDifferenceLines({
    values: recordedValues,
    selectDimension: (v) => v.amount,
    plannedValues: plannedValues.map((v) => v.amount),
    isEquivalent: () => true,
    unconfirmedLine: () => amountUnconfirmedMessage(parameterName),
    unplannedLine: (confirmed) => amountUnplannedMessage(formatAmount(confirmed)),
  });

  // A value counts once however many dimensions it deviates in, so union the implicated values.
  const differingValues = new Set([...material.differingValues, ...amount.differingValues]);

  // A single recorded value with a single difference reads as one bare sentence; a multi-value cell
  // (like data parameters) or a lone value differing in both dimensions splits into two sections.
  return buildDifferencesContent({
    recordedValueCount: recordedValues.length,
    differingValueCount: differingValues.size,
    differingLines: [...material.lines, ...amount.lines],
    allValueLines: recordedValues.map((value) => line([plain(formatMaterialInputValue(value, amountUnit))])),
  });
}

/** A confirmed equipment value recorded against no planned equipment. */
function equipmentUnplannedMessage(parameterName: string): TooltipLine {
  return line([plain(`${parameterName} was confirmed without planned equipment`)]);
}

/** "Confirmed equipment **X** does not match the planned equipment **Y**". */
function equipmentMismatchMessage(confirmed: TypedValue, planned: TypedValue): TooltipLine {
  return differenceLine(
    'Confirmed equipment ',
    formatTypedValue(confirmed),
    ' does not match the planned equipment ',
    formatTypedValue(planned)
  );
}

/** The model deviation. Names no model, since models are compared by id — not something a user would recognize. */
function equipmentModelMismatchMessage(): TooltipLine {
  return line([plain("Confirmed equipment's model does not match the planned equipment's model")]);
}

/**
 * Computes the tooltip content for an equipment Confirmed cell, or null when it should have no
 * tooltip. Equipment is a single dimension (no amount), so each recorded value contributes one line:
 * - no value records at all → the empty rule ("… isn't confirmed yet");
 * - recorded values but nothing planned → "… was confirmed without a planned equipment";
 * - recorded values against a planned equipment → a model or equipment difference line per differing
 *   value; null when nothing differs, a bare sentence for a single value, otherwise two sections.
 */
export function computeEquipmentCellTooltip(params: {
  parameterName: string;
  cellValues: Array<EquipmentValue>;
  plannedValues: Array<EquipmentValue>;
  isEntity: boolean;
}): CellTooltipContent | null {
  const {parameterName, cellValues, plannedValues, isEntity} = params;

  assertAtMostOnePlannedEquipment(parameterName, plannedValues);

  if (cellValues.length === 0) {
    return message(emptyMessage(parameterName, 'confirmed'));
  }

  const recordedValues = cellValues.filter((v) => isPresentTypedValue(v.equipment));
  const plannedEquipment = plannedValues.filter((v) => isPresentTypedValue(v.equipment));

  if (recordedValues.length === 0) {
    // Value record(s) exist but every equipment typed value is empty.
    return null;
  }

  if (plannedEquipment.length === 0) {
    return message(equipmentUnplannedMessage(parameterName));
  }

  const planned = plannedEquipment[0];
  const differingLines = recordedValues
    .map((value) => equipmentDifferenceLine(value, planned, isEntity))
    .filter((l): l is TooltipLine => l != null);

  return buildDifferencesContent({
    recordedValueCount: recordedValues.length,
    differingValueCount: differingLines.length,
    differingLines,
    allValueLines: recordedValues.map((value) => line([plain(formatTypedValue(value.equipment))])),
  });
}

/** The single line describing how one recorded equipment value deviates, or null when it matches. */
function equipmentDifferenceLine(
  confirmed: EquipmentValue,
  planned: EquipmentValue,
  isEntity: boolean
): TooltipLine | null {
  const difference = classifyEquipmentDifference(confirmed, planned, isEntity);
  if (difference == null) {
    return null;
  }
  return difference.kind === 'modelMismatch'
    ? equipmentModelMismatchMessage()
    : equipmentMismatchMessage(confirmed.equipment, planned.equipment);
}

/**
 * The message for an unrecorded material-input cell. There is always one: an unrecorded column reads
 * as "isn't confirmed yet" whether or not a plan exists. A plan that specifies only an amount is the
 * one case that narrows the message to that dimension.
 */
function emptyCellMessage(
  parameterName: string,
  plannedValues: Array<MaterialInputValue>
): CellTooltipContent {
  const plansOnlyAmount =
    presentValues(plannedValues.map((v) => v.material)).length !== 1 &&
    presentValues(plannedValues.map((v) => v.amount)).length === 1;
  return plansOnlyAmount
    ? message(amountUnconfirmedMessage(parameterName))
    : message(emptyMessage(parameterName, 'confirmed'));
}
